import { Box, Spinner, Text, useDisclosure, useToast } from "@chakra-ui/react";
import React, {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  MF_ACCESS_REVIEWS,
  REVIEW_ACCOUNTS_PAGE_SIZE,
} from "state/members/accessReviews";
import { FilterCondition } from "./components/FilterBar";
import { applyFilters } from "./filters/filterRegistry";
import FloatingActionBar from "./Table/FloatingActionBar";
import ReviewModal from "./Table/ReviewModal";
import FixIncorrectDataModal from "./Table/FixIncorrectDataModal";
import VirtualizedGroup from "./Table/VirtualizedGroup";
import * as R from "ramda";
import {
  applyHideReviewed,
  applyHideRemoved,
  bulkCorrectionAccounts,
  bulkRemoveSummary,
  DEFAULT_VISIBLE_COLUMNS,
  expandRowIdsToAccountIds,
  identitySearchTerms,
  isAccountDecided,
  mapAccountIdsToRowIds,
  needsRevokeScopeChoice,
  partitionSettledByAccount,
  partitionStageRows,
  personRowsFromGrouped,
  personSuggestionFor,
  personDecisionTargetIds,
  isAccountDecisionFrozen,
  type FrozenAccountPredicate,
  RAISE_QUERY_ACTION,
  reviewAccountDisplayName,
  sortRowsByName,
  type MFPersonReviewRow,
  personGroupKey,
  personGroupTitle,
  type NameSortDirection,
  personCorrectionAccount,
  type StagedCorrection,
} from "./reviewLogic";
import RevokeScopeModal from "./Table/RevokeScopeModal";
import PreviouslyReviewedSection from "./components/PreviouslyReviewedSection";
import { useColumnWidths } from "./Table/useColumnWidths";
import AccountDrawer from "./components/AccountDrawer";
import QueryModal from "./components/QueryModal";
import { ConfirmModal } from "components/ds";

interface ReviewAccountsTableProps {
  reviewId: string;
  searchQuery: string;
  filterType: string;
  groupBy: string;
  advancedFilters?: FilterCondition[];
  outcomeOptions?: Array<{
    name: string;
    standard_mapping: string;
    requires_note?: boolean;
    // Forwarded to ReviewModal, which reads it to decide whether confirming
    // removes the access now or only lets an admin remove it at approval
    // (PLO-5804). Dropping it here would make the dialog fail closed.
    actions?: Array<{ type?: string | null } | null> | null;
  }>;
  visibleColumns?: string[];
  accountSuggestions?: Record<string, string>; // Map of account IDs to suggestion reasoning
  suggestionActionType?: "approve" | "reject"; // Which recommendation group these suggestions came from
  fillHeight?: boolean; // Whether to fill parent height with flex (for Entitlements tab)
  filterAccountIds?: string[]; // Optional filter to only show specific account IDs
  preSelectedAccounts?: string[]; // Optional pre-selected account IDs
  onReviewSubmitted?: () => void; // Callback when review is submitted
  isResourceOwner?: boolean; // Indicates if the member is the resource owner
  // When true, the cycle only records decisions and never deprovisions
  // (PLO-5083). Forwarded to the rows/drawer/modal so they suppress the
  // "will deprovision immediately" warning, the consent gate, and the
  // irreversible row-lock.
  decisionOnly?: boolean;
  // Locking outcomes this cycle configures with no remediation action, so
  // nothing happened that a re-decide can't undo. Their rows/drawer stay
  // editable in every review state, but the consent gate still fires
  // (PLO-5749).
  reversibleOutcomes?: readonly string[];
  // External modal control props (optional - if not provided, uses internal modal)
  onRequestReview?: (params: {
    accountIds: string[];
    action: string;
    suggestion?: string;
    accountName?: string;
  }) => void; // Callback to request parent to open review modal
  onSelectionChange?: (accountIds: string[]) => void; // Callback when selection changes
  // Entitlement split controls
  splitEntitlementsByType?: boolean;
  onToggleSplitEntitlements?: () => void;
  // When true, all mutations are blocked: no per-row action select, no bulk
  // action bar, no inline entitlement edits. Used after the reviewer has
  // submitted+attested the set so they can't change decisions retroactively.
  isReadOnly?: boolean;
  // When true, already-reviewed accounts are hidden from the table (the
  // hide-reviewed toggle in the all-entitlements tab). Split from filterType so
  // it composes with any active status filter.
  hideReviewed?: boolean;
  // When true (default), accounts decided removed / already-removed are hidden
  // from the table (PLO-4806 hide-removed toggle). Display-only; composes with
  // hideReviewed. Server-derived completion counts are unaffected.
  hideRemoved?: boolean;
  // Only show inline entitlement edit affordances while the fix-data panel is
  // mounted. Review decisions remain available through the regular actions.
  canEditEntitlements?: boolean;
  // The campaign's "Allow users to correct data" setting (PLO-5336). False
  // strips every data-fix affordance from the table — the bulk Fix-incorrect-data
  // menu and the inline entitlement edit — leaving review decisions untouched.
  // Required, with no default: defaulting to `true` fails OPEN, and the call
  // site that forgot it left corrections live on a campaign that had switched
  // them off (PLO-5575). Every renderer states the setting.
  allowCorrections: boolean;
  // Apply entitlement corrections immediately (PLO-4803). The Fix-incorrect-data
  // modal (per-row + bulk) calls this; ReviewDetail persists, logs the applied
  // fix, and opens the corrections panel. Resolves when done.
  onApplyCorrections?: (corrections: StagedCorrection[]) => Promise<void>;
  // Entitlement-only cycle (PLO-5731): every row is ONE entitlement of one
  // account, so a person holding three roles appears as three rows. Drops the
  // flag-for-adjustment action from the rows, the bulk bar and the drawer, and
  // renames the entitlement column and the removal copy accordingly.
  entitlementOnly?: boolean;
  // Float the table's horizontal scrollbar at the viewport bottom (PLO-6217).
  floatingScrollbar?: boolean;
}

// Stand-in for a campaign whose outcomes have not arrived. `actions` says
// "assume it deprovisions": an absent action list would otherwise read as a
// cycle that removes nothing, and silently drop the reviewer's de-provisioning
// warning while the real config is still in flight (PLO-5804).
const defaultOutcomeOptions = [
  { name: "Required", standard_mapping: "required" },
  {
    name: "Not Required",
    standard_mapping: "not_required",
    actions: [{ type: "deprovision" }],
  },
  {
    name: "Out of Scope",
    standard_mapping: "out_of_scope",
    requires_note: true,
  },
];

const EMPTY_ARRAY: string[] = [];
// Module-level so an omitted prop resolves to one shared reference. An inline
// `= []` default mints a new array per render, which voids the memo hooks that
// depend on it and re-runs the mirror effect in useFilters. (PLO-5463)
const EMPTY_FILTERS: FilterCondition[] = [];
const EMPTY_OUTCOME_OPTIONS: ReviewAccountsTableProps["outcomeOptions"] = [];
const EMPTY_SUGGESTIONS: NonNullable<
  ReviewAccountsTableProps["accountSuggestions"]
> = {};

const ReviewAccountsTable: React.FC<ReviewAccountsTableProps> = ({
  reviewId,
  searchQuery,
  filterType,
  groupBy,
  advancedFilters = EMPTY_FILTERS,
  outcomeOptions = EMPTY_OUTCOME_OPTIONS,
  // Fallback only - ReviewDetail always passes an explicit set. Shares the one
  // default rather than mirroring it, so the two can't drift apart (PLO-5119).
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  filterAccountIds,
  preSelectedAccounts = EMPTY_ARRAY,
  onReviewSubmitted,
  accountSuggestions = EMPTY_SUGGESTIONS,
  suggestionActionType,
  fillHeight = false,
  isResourceOwner = false,
  decisionOnly = false,
  reversibleOutcomes,
  onRequestReview,
  onSelectionChange,
  splitEntitlementsByType = false,
  onToggleSplitEntitlements,
  isReadOnly = false,
  hideReviewed = false,
  hideRemoved = true,
  canEditEntitlements = false,
  allowCorrections,
  onApplyCorrections,
  entitlementOnly = false,
  floatingScrollbar = true,
}) => {
  // Default outcome options if none provided by campaign
  // Use custom outcome options if available, otherwise use defaults
  const effectiveOutcomeOptions =
    outcomeOptions.length > 0 ? outcomeOptions : defaultOutcomeOptions;
  // One row per person (PLO-5424): the grouped endpoint folds a person's
  // duplicate identities into one row. Decisions still post account ids to
  // the existing review endpoint — expanded from the person at the mutation
  // boundary below.
  const { data, isLoading } = MF_ACCESS_REVIEWS.useListReviewAccountsGrouped(
    reviewId,
    {},
    REVIEW_ACCOUNTS_PAGE_SIZE,
  );
  const [selectedAccounts, setSelectedAccounts] =
    useState<string[]>(preSelectedAccounts);
  const { mutate: reviewAccounts, isLoading: isReviewing } =
    MF_ACCESS_REVIEWS.useReviewCampaignAccounts(reviewId);
  const { isOpen, onOpen: openModal, onClose: closeModal } = useDisclosure();
  const {
    isOpen: isChangeRolesOpen,
    onOpen: openChangeRolesModal,
    onClose: closeChangeRolesModal,
  } = useDisclosure();
  // State for inline (single-account) entitlement editing
  const {
    isOpen: isInlineEditOpen,
    onOpen: openInlineEdit,
    onClose: closeInlineEdit,
  } = useDisclosure();
  const [inlineEditAccountId, setInlineEditAccountId] = useState<string | null>(
    null,
  );
  // Raise-a-query modal (PLO-4803 P4): the account a query is being raised for.
  const [queryAccountId, setQueryAccountId] = useState<string | null>(null);
  const [isQuerying, setIsQuerying] = useState(false);
  // Bulk raise-a-query over the whole selection (one call, all account ids).
  const [isBulkQueryOpen, setIsBulkQueryOpen] = useState(false);
  // Bulk "mark as non existing" (PLO-4803): confirm + per-account removal.
  const [isBulkRemoveOpen, setIsBulkRemoveOpen] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const removeNonExistent = MF_ACCESS_REVIEWS.useRemoveNonExistentAccount();
  const [reviewAction, setReviewAction] = useState<string | null>(null);
  // The flat account ids the internal modal's submit targets (PLO-5424).
  // Set at action time — the whole person by default, or the chooser's
  // per-identity subset for a revoke on a multi-identity person.
  const [pendingActionAccountIds, setPendingActionAccountIds] = useState<
    string[] | null
  >(null);
  // The revoke awaiting a scope choice on a multi-identity person.
  const [revokeScope, setRevokeScope] = useState<{
    rowId: string;
    action: string;
    suggestion?: string;
    accountName?: string;
  } | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(
    null,
  );
  const [outOfScopeReason, setOutOfScopeReason] = useState("");
  const [note, setNote] = useState("");
  const [markAsRemoved, setMarkAsRemoved] = useState(false);
  const toast = useToast();
  const tableRef = useRef<HTMLDivElement>(null);
  const tableColumns = useMemo(
    () =>
      isReadOnly
        ? visibleColumns.filter((column) => column !== "actions")
        : visibleColumns,
    [isReadOnly, visibleColumns],
  );

  // Combine all pages of data and synthesise one table row per person. Each
  // row keeps the PRIMARY account's id, so selection, the drawer and the
  // stage partition work unchanged; the person extras carry every underlying
  // account + identity.
  const allAccounts: MFPersonReviewRow[] = useMemo(() => {
    if (!data) return [];
    return personRowsFromGrouped(data.pages.flatMap((page) => page.rows));
  }, [data]);

  const rowsById = useMemo(
    () => new Map(allAccounts.map((row) => [row.id, row])),
    [allAccounts],
  );

  // Selection state holds PERSON row ids; the parent talks in flat account
  // ids. Every user-driven selection change routes through here so the parent
  // receives the row expansion (all of a selected person's still-undecided
  // account ids, restricted to filterAccountIds when the card-scoped table
  // sets it). Parent-driven syncs deliberately do NOT re-emit: the parent may
  // hold a per-identity SUBSET of a person (the revoke chooser), and echoing
  // the full expansion back would silently widen its decision.
  const emitSelection = useCallback(
    (rowIds: string[], rows: MFPersonReviewRow[]) => {
      onSelectionChange?.(
        expandRowIdsToAccountIds(rows, rowIds, filterAccountIds),
      );
    },
    [onSelectionChange, filterAccountIds],
  );

  const setSelection = useCallback(
    (rowIds: string[]) => {
      setSelectedAccounts(rowIds);
      emitSelection(rowIds, allAccounts);
    },
    [emitSelection, allAccounts],
  );

  // Update selected rows when preSelectedAccounts changes externally (flat
  // account ids → the person rows containing them). Only sync in controlled
  // mode (onSelectionChange provided), otherwise the component manages its
  // own selection state and parent re-renders would overwrite it.
  useEffect(() => {
    if (!onSelectionChange) return;
    const rowIds = mapAccountIdsToRowIds(allAccounts, preSelectedAccounts);
    setSelectedAccounts((prev) => {
      if (
        prev.length === rowIds.length &&
        prev.every((id) => rowIds.includes(id))
      ) {
        return prev;
      }
      return rowIds;
    });
  }, [preSelectedAccounts, onSelectionChange, allAccounts]);

  // Clear selection when the table flips into read-only mode so leftover
  // checkboxes don't visually suggest the user can still bulk-action.
  useEffect(() => {
    if (isReadOnly && selectedAccounts.length > 0) {
      setSelection([]);
    }
  }, [isReadOnly, selectedAccounts.length, setSelection]);

  // Apply filtering
  // Stage-2 re-review dedup (PLO-4806): when the same person reviews stage 1
  // AND stage 2, the list carries BOTH their decided stage-1 row and its
  // spawned stage-2 successor. Fold the subsumed stage-1 predecessor out of the
  // actionable list (it reappears, read-only, in the "Previously reviewed"
  // disclosure below) so a re-reviewer never sees a confusing duplicate row.
  const stagePartition = useMemo(
    () => partitionStageRows(allAccounts ?? []),
    [allAccounts],
  );
  const previouslyReviewed = stagePartition.previouslyReviewed;

  // Rows load A-Z by Account name so a person's rows sit together; the
  // Account header flips it to Z-A. Not persisted: every load starts A-Z
  // (PLO-6218). The table stays mounted when the route moves to another
  // review, so a flip is kept against the review it was made on and any
  // other review reads A-Z.
  const [nameSortFlip, setNameSortFlip] = useState<{
    reviewId: string;
    direction: NameSortDirection;
  } | null>(null);
  const nameSort: NameSortDirection =
    nameSortFlip?.reviewId === reviewId ? nameSortFlip.direction : "asc";
  const toggleNameSort = useCallback(
    () =>
      setNameSortFlip({
        reviewId,
        direction: nameSort === "asc" ? "desc" : "asc",
      }),
    [reviewId, nameSort],
  );

  const filteredAccounts = useMemo(() => {
    if (!allAccounts) return [];

    let filtered = [...stagePartition.active];

    // Apply account ID filter if provided. The ids are flat account ids (AI
    // recommendation buckets) — a person row matches when ANY of its
    // underlying accounts is in the set.
    if (filterAccountIds && filterAccountIds.length > 0) {
      filtered = filtered.filter((account) =>
        account.personAccountIds.some((id) => filterAccountIds.includes(id)),
      );
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (account) =>
          account.member.name?.toLowerCase().includes(query) ||
          account.member.email?.toLowerCase().includes(query) ||
          account.member.department?.toLowerCase().includes(query) ||
          account.member.jobTitle?.toLowerCase().includes(query) ||
          account.resource.name?.toLowerCase().includes(query) ||
          identitySearchTerms(account).some((term) =>
            term.toLowerCase().includes(query),
          ),
      );
    }

    // Apply status/review filter
    if (filterType !== "all") {
      switch (filterType) {
        case "unreviewed":
          filtered = filtered.filter((account) => !account.review);
          break;
        case "reviewed":
          filtered = filtered.filter((account) => account.review);
          break;
        case "required":
          filtered = filtered.filter(
            (account) => account.review === "required",
          );
          break;
        case "not_required":
          filtered = filtered.filter(
            (account) => account.review === "not_required",
          );
          break;
        case "out_of_scope":
          filtered = filtered.filter(
            (account) => account.review === "out_of_scope",
          );
          break;
      }
    }

    // Apply advanced filters
    if (advancedFilters && advancedFilters.length > 0) {
      filtered = filtered.filter((account) =>
        applyFilters(account, advancedFilters),
      );
    }

    // Hide-reviewed toggle — drops accounts that already carry an outcome.
    filtered = applyHideReviewed(filtered, hideReviewed);

    // Hide-removed toggle (default on) — drops decided-removed accounts. Applied
    // after hide-reviewed so both compose (both on = both filters).
    filtered = applyHideRemoved(filtered, hideRemoved);

    return sortRowsByName(filtered, nameSort);
  }, [
    allAccounts,
    stagePartition,
    searchQuery,
    filterType,
    advancedFilters,
    filterAccountIds,
    hideReviewed,
    hideRemoved,
    nameSort,
  ]);

  // Apply grouping
  const groupedAccounts = useMemo(() => {
    if (!filteredAccounts.length) return [{ title: "", accounts: [] }];

    if (groupBy === "none") {
      return [{ title: "", accounts: filteredAccounts }];
    }

    // Keyed apart from the title: a person group is keyed by who it is, and
    // only titled by their name (`personGroupKey`).
    const groups: Record<
      string,
      { title: string; accounts: MFPersonReviewRow[] }
    > = {};

    filteredAccounts.forEach((account) => {
      let groupKey = "";
      let title: string | null = null;

      switch (groupBy) {
        case "department":
          groupKey = account.member.department || "No Department";
          break;
        case "jobTitle":
          groupKey = account.member.jobTitle || "No Job Title";
          break;
        // Grouped on account.accessLevel before, which held the entitlement
        // names anyway. Same grouping, read from the field that actually means it.
        case "accessLevel":
          groupKey =
            (account.entitlements || [])
              .map((e) => e.snapshotEntitlementName)
              .filter(Boolean)
              .join(", ") || "No Entitlements";
          break;
        case "status":
          groupKey = account.review
            ? `Review: ${account.review}`
            : "Not reviewed";
          break;
        case "person":
          groupKey = personGroupKey(account);
          title = personGroupTitle(account);
          break;
        default:
          groupKey = "All Accounts";
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { title: title ?? groupKey, accounts: [] };
      }

      groups[groupKey].accounts.push(account);
    });

    // Sort groups by name; two people sharing one stay in a stable order.
    return Object.entries(groups)
      .sort(
        ([keyA, a], [keyB, b]) =>
          a.title.localeCompare(b.title) || keyA.localeCompare(keyB),
      )
      .map(([, group]) => group);
  }, [filteredAccounts, groupBy]);

  // Persisted drag-to-resize column widths (PLO-4818, item 5).
  const { columnWidths, onResizeColumn } = useColumnWidths(reviewId);

  // Account detail drawer (PLO-4818, item 4). `orderedAccountIds` is the same
  // flat, filtered, display-ordered list the rows render from — so the drawer's
  // prev/next walks exactly what the reviewer sees, across groups.
  const [drawerAccountId, setDrawerAccountId] = useState<string | null>(null);
  const orderedAccountIds = useMemo(
    () => groupedAccounts.flatMap((g) => g.accounts.map((a) => a.id)),
    [groupedAccounts],
  );
  const drawerAccount = useMemo(
    () =>
      drawerAccountId
        ? (filteredAccounts.find((a) => a.id === drawerAccountId) ?? null)
        : null,
    [drawerAccountId, filteredAccounts],
  );
  // When a filter removes the drawer's row, the drawer closes (drawerAccount
  // resolves to null) but the id lingers — clearing the filter would re-resolve
  // it and ghost-reopen the drawer. Drop the retained id the moment its row
  // leaves the filtered set so it can't reopen.
  useEffect(() => {
    if (
      drawerAccountId &&
      !filteredAccounts.some((a) => a.id === drawerAccountId)
    ) {
      setDrawerAccountId(null);
    }
  }, [drawerAccountId, filteredAccounts]);
  const openAccount = useCallback((id: string) => setDrawerAccountId(id), []);

  const handleSelectAccount = useCallback(
    (id: string, checked: boolean, newSelectedState?: string[]) => {
      if (isReadOnly) return;

      setSelectedAccountName(null);

      if (newSelectedState) {
        // This is from bulk selection
        setSelection(newSelectedState);
        return;
      }

      if (id) {
        // Individual selection
        if (checked) {
          setSelection([...selectedAccounts, id]);
        } else {
          setSelection(selectedAccounts.filter((accId) => accId !== id));
        }
      }
    },
    [isReadOnly, selectedAccounts, setSelection],
  );

  // Only prefill suggestion reasoning when the action aligns with the recommendation
  const isAffirmativeAction = useCallback(
    (action: string) => {
      if (!suggestionActionType) return true;
      if (suggestionActionType === "approve") return action === "required";
      if (suggestionActionType === "reject") return action === "not_required";
      return false;
    },
    [suggestionActionType],
  );

  // Continue an action into the decision flow with an explicit flat
  // account-id target list (the whole person, or the chooser's subset).
  const proceedWithAction = useCallback(
    (
      row: MFPersonReviewRow,
      accountIds: string[],
      action: string,
      suggestion?: string,
      accountName?: string,
    ) => {
      if (accountIds.length === 0) return;

      // If external review handler is provided, use it
      if (onRequestReview) {
        onRequestReview({
          accountIds,
          action,
          suggestion,
          accountName,
        });
      } else {
        // Otherwise use internal modal
        setPendingActionAccountIds(accountIds);
        setSelectedAccounts([row.id]);
        setSelectedAccountName(accountName ?? null);
        setReviewAction(action);
        setOutOfScopeReason("");
        setMarkAsRemoved(false);
        setNote(suggestion || "");
        openModal();
      }
    },
    [onRequestReview, openModal],
  );

  // An identity whose recorded outcome started de-provisioning is frozen: a
  // re-decision on its person must never reach it, even when its siblings are
  // re-decided (PLO-5749). Same rule the row's own lock uses.
  const isFrozenAccount = useCallback<FrozenAccountPredicate>(
    (account) =>
      isAccountDecisionFrozen(account, { decisionOnly, reversibleOutcomes }),
    [decisionOnly, reversibleOutcomes],
  );

  const handleSelectAction = useCallback(
    (id: string, action: string) => {
      if (isReadOnly) return;

      // "Raise a query" is not an outcome — it opens the query modal, which
      // posts review:"query" with the reviewer's note as the question.
      if (action === RAISE_QUERY_ACTION) {
        setQueryAccountId(id);
        return;
      }

      const row = rowsById.get(id);
      if (!row) return;

      const suggestion = isAffirmativeAction(action)
        ? personSuggestionFor(row, accountSuggestions)
        : undefined;
      const accountName = reviewAccountDisplayName(row);

      // A revoke on a person with several actionable identities must first
      // say which identities it covers (PLO-5424). Approvals and other
      // outcomes always act on the whole person.
      if (
        needsRevokeScopeChoice(row, action, filterAccountIds, isFrozenAccount)
      ) {
        setRevokeScope({ rowId: id, action, suggestion, accountName });
        return;
      }

      // Undecided identities, or — once every identity is decided — the
      // unfrozen ones again, so a reviewer can change their mind (PLO-5749).
      proceedWithAction(
        row,
        personDecisionTargetIds(row, filterAccountIds, isFrozenAccount),
        action,
        suggestion,
        accountName,
      );
    },
    [
      accountSuggestions,
      isAffirmativeAction,
      isReadOnly,
      rowsById,
      filterAccountIds,
      isFrozenAccount,
      proceedWithAction,
    ],
  );

  // The chooser confirmed which identities the revoke covers.
  const handleRevokeScopeConfirm = useCallback(
    (accountIds: string[]) => {
      if (!revokeScope) return;
      const row = rowsById.get(revokeScope.rowId);
      setRevokeScope(null);
      if (!row) return;
      proceedWithAction(
        row,
        accountIds,
        revokeScope.action,
        revokeScope.suggestion,
        revokeScope.accountName,
      );
    },
    [revokeScope, rowsById, proceedWithAction],
  );

  // Decides `rowIds` together through the same review modal a bulk decision
  // uses: the bulk bar passes the selection, a person group's header passes
  // that person's undecided rows.
  const decideRows = useCallback(
    (rowIds: string[], action: string) => {
      if (isReadOnly) return;

      const firstSelectedRow =
        rowIds.length > 0 ? rowsById.get(rowIds[0]) : undefined;
      const firstSuggestion =
        isAffirmativeAction(action) && firstSelectedRow
          ? personSuggestionFor(firstSelectedRow, accountSuggestions)
          : undefined;

      // A bulk decision covers every selected person wholly — expand the
      // selected rows to their still-undecided account ids.
      const targetAccountIds = expandRowIdsToAccountIds(
        allAccounts,
        rowIds,
        filterAccountIds,
      );

      // If external review handler is provided, use it
      if (onRequestReview) {
        onRequestReview({
          accountIds: targetAccountIds,
          action,
          suggestion: firstSuggestion,
        });
        // Selection stays intact while the modal is open — the parent owns
        // the lifecycle and will clear it after submit. Clearing here would
        // fire the `onSelectionChange` effect and overwrite the parent's
        // freshly-set selection back to [], leaving the modal with 0 rows.
      } else {
        // Otherwise use internal modal
        setPendingActionAccountIds(targetAccountIds);
        setSelectedAccountName(null);
        setReviewAction(action);
        setOutOfScopeReason("");
        setMarkAsRemoved(false);
        setNote(firstSuggestion || "");
        openModal();
      }
    },
    [
      onRequestReview,
      openModal,
      accountSuggestions,
      isAffirmativeAction,
      isReadOnly,
      rowsById,
      allAccounts,
      filterAccountIds,
    ],
  );

  const handleBulkAction = useCallback(
    (action: string) => decideRows(selectedAccounts, action),
    [decideRows, selectedAccounts],
  );

  // Handle inline entitlement editing for a single account row
  const handleEditEntitlements = useCallback(
    (accountId: string) => {
      if (isReadOnly) return;
      setInlineEditAccountId(accountId);
      openInlineEdit();
    },
    [openInlineEdit, isReadOnly],
  );

  // Inline corrections edit the PRIMARY underlying account (see
  // bulkFixAccounts above for why never the synthesised person row).
  const inlineEditAccount = useMemo(() => {
    if (!inlineEditAccountId) return null;
    const row = rowsById.get(inlineEditAccountId);
    if (!row) return null;
    return personCorrectionAccount(row, filterAccountIds);
  }, [inlineEditAccountId, rowsById, filterAccountIds]);

  // Stable single-element array for the inline modal so its seed effect doesn't
  // refire (and wipe in-progress edits) on every parent render.
  const inlineEditAccounts = useMemo(
    () => (inlineEditAccount ? [inlineEditAccount] : []),
    [inlineEditAccount],
  );

  // Accounts targeted by the bulk Fix-incorrect-data modal: the current
  // selection minus any locked (already de-provisioning) rows and any indirect
  // row (the server rejects a correction on one). Corrections full-replace ONE
  // account's entitlement set, so each person contributes their PRIMARY
  // underlying account — never the synthesised row, whose entitlements are
  // the person-wide union.
  const bulkFixAccounts = useMemo(
    () =>
      bulkCorrectionAccounts(allAccounts, selectedAccounts, filterAccountIds),
    [allAccounts, selectedAccounts, filterAccountIds],
  );

  // The flat account ids the bulk query / bulk mark-as-non-existing fan out
  // over: every still-undecided account of every selected person.
  const bulkTargetAccountIds = useMemo(
    () =>
      expandRowIdsToAccountIds(allAccounts, selectedAccounts, filterAccountIds),
    [allAccounts, selectedAccounts, filterAccountIds],
  );

  // Apply a bulk correction (persists), then clear the selection. The modal
  // closes itself once onApply resolves.
  const handleBulkFixApply = useCallback(
    async (corrections: StagedCorrection[]) => {
      if (isReadOnly) return;
      await onApplyCorrections?.(corrections);
      setSelection([]);
    },
    [isReadOnly, onApplyCorrections, setSelection],
  );

  // Apply a single-account correction (persists), then clear the inline target.
  const handleInlineFixApply = useCallback(
    async (corrections: StagedCorrection[]) => {
      if (isReadOnly) return;
      await onApplyCorrections?.(corrections);
      setInlineEditAccountId(null);
    },
    [isReadOnly, onApplyCorrections],
  );

  // The account a query is being raised for (for the modal subtitle).
  const queryAccount = useMemo(
    () =>
      queryAccountId
        ? (allAccounts.find((a) => a.id === queryAccountId) ?? null)
        : null,
    [queryAccountId, allAccounts],
  );

  // Raise a query: posts review:"query" with the reviewer's note (the question)
  // via the same review mutation the decision flow uses.
  const handleRaiseQuery = useCallback(
    async (note: string) => {
      if (isReadOnly || !queryAccountId) return;
      // The query covers the whole person: every still-undecided account, or
      // the unfrozen decided ones when the person is already fully decided
      // (PLO-5749) — a query on a decided row used to resolve to nothing.
      const row = rowsById.get(queryAccountId);
      const accountIds = row
        ? personDecisionTargetIds(row, filterAccountIds, isFrozenAccount)
        : [];
      if (accountIds.length === 0) return;
      setIsQuerying(true);
      try {
        await reviewAccounts({
          reviewId,
          accountIds,
          review: "query",
          note,
        });
        toast({
          title: "Query sent",
          description: "The campaign admin will follow up on this account.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
        setQueryAccountId(null);
        onReviewSubmitted?.();
      } catch (error: any) {
        toast({
          title: "Couldn't send query",
          description: error?.message || "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      } finally {
        setIsQuerying(false);
      }
    },
    [
      isReadOnly,
      queryAccountId,
      rowsById,
      filterAccountIds,
      isFrozenAccount,
      reviewId,
      reviewAccounts,
      toast,
      onReviewSubmitted,
    ],
  );

  // Bulk raise a query: ONE review call carrying every selected account id (the
  // endpoint accepts an array — no per-account loop). The selection expands to
  // every still-undecided account of every selected person.
  const handleBulkRaiseQuery = useCallback(
    async (note: string) => {
      const ids = bulkTargetAccountIds;
      if (isReadOnly || ids.length === 0) return;
      setIsQuerying(true);
      try {
        await reviewAccounts({
          reviewId,
          accountIds: ids,
          review: "query",
          note,
        });
        toast({
          title: "Query sent",
          description: `Raised a query on ${ids.length} ${ids.length === 1 ? "account" : "accounts"}.`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
        setIsBulkQueryOpen(false);
        setSelection([]);
        onReviewSubmitted?.();
      } catch (error: any) {
        toast({
          title: "Couldn't send query",
          description: error?.message || "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      } finally {
        setIsQuerying(false);
      }
    },
    [
      isReadOnly,
      bulkTargetAccountIds,
      reviewId,
      reviewAccounts,
      toast,
      onReviewSubmitted,
      setSelection,
    ],
  );

  // Bulk mark-as-non-existing: one remove-non-existent call per account —
  // every still-undecided account of every selected person. The server
  // rejects self-held / already-corrected accounts, so we tolerate
  // per-account failure and surface an honest succeeded/failed summary.
  const handleBulkMarkNonExistent = useCallback(async () => {
    const ids = bulkTargetAccountIds;
    if (isReadOnly || ids.length === 0) return;
    setIsBulkRemoving(true);
    try {
      const settled = await Promise.allSettled(
        ids.map((id) => removeNonExistent.mutate(id)),
      );
      const outcome = partitionSettledByAccount(ids, settled);
      toast({
        title:
          outcome.failed.length === 0
            ? "Accounts marked as not existing"
            : "Some accounts couldn't be corrected",
        description: bulkRemoveSummary(outcome),
        status: outcome.succeeded.length > 0 ? "success" : "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
      if (outcome.succeeded.length > 0) setSelection([]);
      onReviewSubmitted?.();
    } finally {
      setIsBulkRemoving(false);
    }
  }, [
    isReadOnly,
    bulkTargetAccountIds,
    removeNonExistent,
    toast,
    onReviewSubmitted,
    setSelection,
  ]);

  const submitReview = useCallback(() => {
    if (!reviewAction) return;

    // The action set at click time (whole person, or the revoke chooser's
    // per-identity subset); a bare checkbox selection expands to every
    // selected person's still-undecided accounts. The undecided filter also
    // drops anything decided since — a safety measure, as before.
    const decidedIds = new Set(
      allAccounts.flatMap((row) =>
        row.personAccounts.filter((a) => isAccountDecided(a)).map((a) => a.id),
      ),
    );
    const targetAccountIds = (
      pendingActionAccountIds ??
      expandRowIdsToAccountIds(allAccounts, selectedAccounts, filterAccountIds)
    ).filter((id) => !decidedIds.has(id));

    if (targetAccountIds.length === 0) return;

    reviewAccounts({
      reviewId,
      accountIds: targetAccountIds,
      review: reviewAction,
      outOfScopeReason:
        reviewAction === "out_of_scope" ? outOfScopeReason : undefined,
      note: note || undefined,
      markAsRemoved: markAsRemoved || undefined,
    }).then(() => {
      toast({
        // Never "submitted": a decision is not a submission, and reviewers who
        // read it that way left without submitting (PLO-6222).
        title: "Decision saved",
        description: `Reviewed ${targetAccountIds.length} ${targetAccountIds.length === 1 ? "account" : "accounts"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      setPendingActionAccountIds(null);
      setSelection([]);
      setSelectedAccountName(null);
      closeModal();

      // Call the callback if provided
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    });
  }, [
    reviewAction,
    selectedAccounts,
    pendingActionAccountIds,
    filterAccountIds,
    reviewId,
    outOfScopeReason,
    note,
    markAsRemoved,
    reviewAccounts,
    toast,
    closeModal,
    onReviewSubmitted,
    allAccounts,
    setSelection,
  ]);

  if (isLoading) {
    return (
      <Box py={8} textAlign="center">
        <Spinner size="sm" color="text.body.tertiary" />
      </Box>
    );
  }

  if (!filteredAccounts || filteredAccounts.length === 0) {
    return (
      <Box
        p={6}
        textAlign="center"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="component.2xl"
        bg="bg.section"
        minH="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="text.body.secondary">
          No accounts found matching your criteria.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      width="full"
      position="relative"
      ref={tableRef}
      display="flex"
      flexDirection="column"
      flex={fillHeight ? "1" : undefined}
      minH={fillHeight ? 0 : undefined}
      gap={4}
    >
      {/* Groups. Wrapped apart from the previously-reviewed list below: a
          stuck single-group table (PLO-6216) moves within its parent, and must
          not slide over that list. */}
      <Box
        display="flex"
        flexDirection="column"
        flex={fillHeight ? "1" : undefined}
        minH={fillHeight ? 0 : undefined}
        gap={4}
      >
        {groupedAccounts.map((group, idx) => (
          <VirtualizedGroup
            key={idx}
            group={group}
            selectedAccounts={selectedAccounts}
            handleSelectAccount={handleSelectAccount}
            handleSelectAction={handleSelectAction}
            handleEditEntitlements={handleEditEntitlements}
            outcomeOptions={effectiveOutcomeOptions}
            visibleColumns={tableColumns}
            shouldFillSpace={fillHeight && groupedAccounts.length === 1}
            splitEntitlementsByType={splitEntitlementsByType}
            onToggleSplitEntitlements={onToggleSplitEntitlements}
            canEditEntitlements={
              canEditEntitlements && !isReadOnly && allowCorrections
            }
            columnWidths={columnWidths}
            onResizeColumn={onResizeColumn}
            onOpenAccount={openAccount}
            decisionOnly={decisionOnly}
            reversibleOutcomes={reversibleOutcomes}
            entitlementOnly={entitlementOnly}
            floatingScrollbar={floatingScrollbar}
            nameSort={nameSort}
            onToggleNameSort={toggleNameSort}
            personGrouped={groupBy === "person"}
            onGroupAction={decideRows}
          />
        ))}
      </Box>

      {/* Stage-1 predecessors folded out of the actionable list (PLO-4806) —
          shown read-only under a collapsed disclosure so the audit trail of
          "what was decided at the earlier stage" stays reachable without
          duplicating the person as an actionable row. */}
      <PreviouslyReviewedSection
        accounts={previouslyReviewed}
        outcomeOptions={effectiveOutcomeOptions}
      />

      {/* Per-account detail drawer with prev/next across the ordered list. Its
          footer mirrors the row's decision actions via the same handler. */}
      <AccountDrawer
        account={drawerAccount}
        isOpen={drawerAccount !== null}
        onClose={() => setDrawerAccountId(null)}
        orderedIds={orderedAccountIds}
        onNavigate={setDrawerAccountId}
        outcomeOptions={effectiveOutcomeOptions}
        isReadOnly={isReadOnly}
        onAction={handleSelectAction}
        decisionOnly={decisionOnly}
        reversibleOutcomes={reversibleOutcomes}
        entitlementOnly={entitlementOnly}
      />

      {/* Floating action bar — hidden when the set is locked (post-submit /
          post-attest). Read-only mode also gates every action handler so a
          stale selection can't fire mutations. */}
      <FloatingActionBar
        selectedCount={selectedAccounts.length}
        onAction={handleBulkAction}
        onChangeRoles={
          allowCorrections && bulkFixAccounts.length > 0
            ? openChangeRolesModal
            : undefined
        }
        onBulkMarkNonExistent={
          allowCorrections ? () => setIsBulkRemoveOpen(true) : undefined
        }
        onRaiseQuery={() => setIsBulkQueryOpen(true)}
        isVisible={!isReadOnly && selectedAccounts.length > 0}
        isLoading={isReviewing || isBulkRemoving}
        outcomeOptions={effectiveOutcomeOptions}
        selectedAccounts={allAccounts.filter(
          (acc) => selectedAccounts.includes(acc.id) && !isAccountDecided(acc),
        )}
        entitlementOnly={entitlementOnly}
      />

      {/* Review modal - only render if not using external modal control */}
      {!onRequestReview && (
        <ReviewModal
          isOpen={isOpen}
          onClose={() => {
            closeModal();
            setSelectedAccountName(null);
            setPendingActionAccountIds(null);
          }}
          reviewAction={reviewAction}
          selectedCount={
            pendingActionAccountIds?.length ?? selectedAccounts.length
          }
          selectedAccountName={selectedAccountName ?? undefined}
          outOfScopeReason={outOfScopeReason}
          setOutOfScopeReason={setOutOfScopeReason}
          note={note}
          setNote={setNote}
          onSubmit={submitReview}
          isSubmitting={isReviewing}
          outcomeOptions={effectiveOutcomeOptions}
          isResourceOwner={isResourceOwner}
          markAsRemoved={markAsRemoved}
          setMarkAsRemoved={setMarkAsRemoved}
          decisionOnly={decisionOnly}
          entitlementOnly={entitlementOnly}
        />
      )}

      {/* Bulk Fix-incorrect-data modal (tri-state) — stages drafts across the
          selection; the corrections persist from ReviewDetail's panel. */}
      <FixIncorrectDataModal
        isOpen={isChangeRolesOpen}
        onClose={closeChangeRolesModal}
        selectedAccounts={bulkFixAccounts}
        onApply={handleBulkFixApply}
        isLocked={isReadOnly}
      />

      {/* Inline (single-account) Fix-incorrect-data modal — always rendered to
          avoid a mount flash. Stages a draft for the one account. */}
      <FixIncorrectDataModal
        isOpen={isInlineEditOpen}
        onClose={() => {
          closeInlineEdit();
          setInlineEditAccountId(null);
        }}
        selectedAccounts={inlineEditAccounts}
        onApply={handleInlineFixApply}
        isLocked={isReadOnly}
      />

      {/* Raise-a-query modal (mandatory note) — single row. */}
      <QueryModal
        isOpen={queryAccountId !== null}
        onClose={() => setQueryAccountId(null)}
        accountName={
          queryAccount ? reviewAccountDisplayName(queryAccount) : undefined
        }
        isSubmitting={isQuerying}
        isLocked={isReadOnly}
        onSubmit={handleRaiseQuery}
      />

      {/* Bulk raise-a-query modal — one call over the whole selection,
          expanded to every selected person's undecided accounts. */}
      <QueryModal
        isOpen={isBulkQueryOpen}
        onClose={() => setIsBulkQueryOpen(false)}
        accountName={`${bulkTargetAccountIds.length} ${
          bulkTargetAccountIds.length === 1 ? "account" : "accounts"
        }`}
        isSubmitting={isQuerying}
        isLocked={isReadOnly}
        onSubmit={handleBulkRaiseQuery}
      />

      {/* Per-identity scope chooser for a revoke on a multi-identity person
          (PLO-5424). Confirming continues into the regular decision flow. */}
      <RevokeScopeModal
        isOpen={revokeScope !== null}
        onClose={() => setRevokeScope(null)}
        personName={revokeScope?.accountName ?? "This person"}
        actionName={
          effectiveOutcomeOptions.find(
            (o) => o.standard_mapping === revokeScope?.action,
          )?.name ?? "Revoke"
        }
        row={revokeScope ? (rowsById.get(revokeScope.rowId) ?? null) : null}
        scope={filterAccountIds}
        isFrozen={isFrozenAccount}
        onConfirm={handleRevokeScopeConfirm}
      />

      {/* Bulk "mark as non existing" confirmation — a data fix, not a review
          decision. */}
      {isBulkRemoveOpen && !isReadOnly && (
        <ConfirmModal
          title={`Mark ${bulkTargetAccountIds.length} ${
            bulkTargetAccountIds.length === 1 ? "account" : "accounts"
          } as not existing?`}
          body="This records that the selected people don't have this access at source. Their rows are marked already removed and the access is recorded as revoked. This fixes incorrect data; it is not a review decision. Accounts you can't correct (your own, or already-corrected ones) are skipped."
          confirmLabel="Mark as not existing"
          confirmTone="danger"
          onConfirm={() => {
            setIsBulkRemoveOpen(false);
            void handleBulkMarkNonExistent();
          }}
          onCancel={() => setIsBulkRemoveOpen(false)}
        />
      )}
    </Box>
  );
};

export default memo(ReviewAccountsTable);
